import React from 'react';
import { Rack, RackType, Server, ServerAgeStage } from '../types';
import { SERVER_TYPES, TOR_SWITCH_TYPES, MPO_PANEL_TYPES, MGMT_SWITCH_TYPES } from '../constants';

interface RackInteriorViewProps {
  rack: Rack;
  rackType: RackType;
  gameTime: number;
  onRepair?: (serverId: string) => void;
  formatCurrency?: (amount: number, decimals?: number) => string;
  networkStatus?: { mmrBuilt: boolean; locationHasCore: boolean; floorHasHda: boolean; externalConnectivity: number };
  thermalStrain?: number; // 0–1 location cooling utilisation; tints rows warm when high
  isPaused?: boolean;     // freezes the status-LED animations while the sim is paused
}

// Per-server status lights: a steady-ish power LED + a NIC activity LED that blinks
// faster the busier the server is. Pure CSS animation — no game-loop cost.
const ServerLeds: React.FC<{ server: Server; paused?: boolean }> = ({ server, paused = false }) => {
  const failed = server.failureStatus === 'failed';
  const repairing = server.failureStatus === 'repairing';
  const online = !failed && !repairing;

  // While paused, freeze animations in place rather than restarting them.
  const playState = paused ? 'paused' : 'running';

  // Power LED: green breathing when online, amber pulse when repairing, dark red when failed
  const powerColor = failed ? '#7f1d1d' : repairing ? '#f59e0b' : '#22c55e';
  const powerAnim = online
    ? 'riv-breathe 2.6s ease-in-out infinite'
    : repairing ? 'riv-blink 0.9s steps(2,start) infinite' : 'none';

  // NIC activity LED: blink rate scales with utilisation (busy = faster). Off when not online.
  const util = Math.max(0, Math.min(1.15, server.utilizationRate ?? 0.75));
  const nicMs = Math.round(1300 - util * 950); // ~1300ms idle → ~350ms hot
  return (
    <span className="flex items-center gap-[3px] shrink-0">
      <span
        title="Power"
        style={{ width: 5, height: 5, borderRadius: 9999, background: powerColor, animation: powerAnim, animationPlayState: playState, boxShadow: online ? '0 0 3px #22c55e' : undefined }}
      />
      <span
        title="Network activity"
        style={{
          width: 5, height: 5, borderRadius: 9999,
          background: online ? '#22d3ee' : '#334155',
          animation: online ? `riv-blink ${nicMs}ms steps(2,start) infinite` : 'none',
          animationPlayState: playState,
        }}
      />
    </span>
  );
};

// U slots reserved for infrastructure (cannot hold servers)
// Displayed at the top of the rack (highest U numbers)
const RESERVED_SLOTS: Record<number, { label: string; cls: string; textCls: string }> = {
  48: { label: 'MPO / Uplinks',             cls: 'bg-slate-700 border-slate-500',   textCls: 'text-slate-300' },
  47: { label: '— Airflow gap —',           cls: 'bg-gray-800 border-gray-700',     textCls: 'text-gray-600' },
  46: { label: 'TOR Switch (primary)',       cls: 'bg-blue-900 border-blue-700',     textCls: 'text-blue-300' },
  45: { label: 'TOR Switch (redundant)',     cls: 'bg-blue-950 border-blue-800',     textCls: 'text-blue-500' },
  44: { label: 'Mgmt Switch — none',         cls: 'bg-gray-800 border-amber-700/50', textCls: 'text-amber-500/70' },
};

// First usable server U (U43 and below)
export const RACK_SERVER_MAX_U = 43;

function serverSlotColor(server: Server): string {
  if (server.failureStatus === 'failed') return 'bg-red-800 border-red-500';
  if (server.failureStatus === 'repairing') return 'bg-amber-800 border-amber-500';
  const age = server.ageStage ?? 'new';
  if (age === 'eol') return 'bg-orange-800 border-orange-500';
  if (age === 'aging') return 'bg-yellow-800 border-yellow-600';
  return 'bg-emerald-900 border-emerald-700';
}

function serverSlotText(server: Server): string {
  if (server.failureStatus === 'failed') return 'text-red-300';
  if (server.failureStatus === 'repairing') return 'text-amber-300';
  const age = server.ageStage ?? 'new';
  if (age === 'eol') return 'text-orange-300';
  if (age === 'aging') return 'text-yellow-300';
  return 'text-emerald-400';
}

const AGE_LABEL: Record<ServerAgeStage, string> = {
  new: 'New',
  mature: 'Mature',
  aging: 'Aging',
  eol: 'End of Life',
};

const RackInteriorView: React.FC<RackInteriorViewProps> = ({
  rack,
  rackType,
  gameTime,
  onRepair,
  formatCurrency,
  networkStatus,
  thermalStrain = 0,
  isPaused = false,
}) => {
  const servers = rack.servers ?? [];
  const [hoveredU, setHoveredU] = React.useState<number | null>(null);

  // Thermal tint applied to all server rows when cooling is strained
  const thermalTint = thermalStrain >= 0.95
    ? 'linear-gradient(rgba(239,68,68,0.28),rgba(239,68,68,0.28))'   // critical — red
    : thermalStrain >= 0.85
      ? 'linear-gradient(rgba(251,146,60,0.22),rgba(251,146,60,0.22))' // strained — orange
      : undefined;

  // Map servers to U slots, filling from U43 downward (top of server area first)
  // Each server occupies uSize consecutive U slots
  const serverByU = new Map<number, Server>(); // lowest U of the server → server
  let nextU = RACK_SERVER_MAX_U;
  for (const server of servers) {
    const st = SERVER_TYPES.find(t => t.id === server.type);
    const size = st?.uSize ?? 1;
    if (nextU - size + 1 >= 1) {
      serverByU.set(nextU, server);
      nextU -= size;
    }
  }

  const mpoPanel = rack.mpoPatchPanel
    ? MPO_PANEL_TYPES.find(p => p.id === rack.mpoPatchPanel!.typeId)
    : null;
  const torSwitch = rack.torSwitch
    ? TOR_SWITCH_TYPES.find(t => t.id === rack.torSwitch!.typeId)
    : null;
  const torSwitchRedundant = rack.torSwitchRedundant
    ? TOR_SWITCH_TYPES.find(t => t.id === rack.torSwitchRedundant!.typeId)
    : null;
  const mgmtSwitch = rack.managementSwitch
    ? MGMT_SWITCH_TYPES.find(t => t.id === rack.managementSwitch!.typeId)
    : null;

  // Mark U slots consumed by multi-U servers so the loop can skip them
  const consumedU = new Set<number>();
  for (const [topU, server] of Array.from(serverByU.entries())) {
    const st = SERVER_TYPES.find(t => t.id === server.type);
    const size = st?.uSize ?? 1;
    for (let i = 1; i < size; i++) consumedU.add(topU - i);
  }

  // Build rows top→bottom: U48 → U1
  const rows: React.ReactNode[] = [];

  for (let u = 48; u >= 1; u--) {
    if (consumedU.has(u)) continue; // lower part of a multi-U server, already rendered

    const uLabel = String(u).padStart(2, '0');
    const isHovered = hoveredU === u;

    // Reserved infrastructure slots
    if (RESERVED_SLOTS[u]) {
      const r = RESERVED_SLOTS[u];
      let displayLabel = r.label;
      let extraCls = '';
      let cls = r.cls;
      let textCls = r.textCls;
      if (u === 48 && mpoPanel) {
        displayLabel = mpoPanel.name;
        extraCls = 'font-semibold';
      } else if (u === 46 && torSwitch) {
        displayLabel = torSwitch.name;
        extraCls = 'font-semibold';
      } else if (u === 45 && torSwitchRedundant) {
        displayLabel = `${torSwitchRedundant.name} (MLAG)`;
        extraCls = 'font-semibold';
      } else if (u === 44 && mgmtSwitch) {
        displayLabel = mgmtSwitch.name;
        extraCls = 'font-semibold';
        cls = 'bg-indigo-950 border-indigo-700';
        textCls = 'text-indigo-300';
      }
      rows.push(
        <div
          key={`u${u}`}
          className={`flex items-center gap-1.5 h-[18px] border-b border-gray-800/50`}
        >
          <span className="w-6 text-right text-[9px] text-gray-600 shrink-0 font-mono">{uLabel}</span>
          <div className={`flex-1 h-[14px] rounded-sm border ${cls} flex items-center px-1.5`}>
            <span className={`text-[9px] leading-none truncate ${textCls} ${extraCls}`}>{displayLabel}</span>
          </div>
        </div>
      );
      continue;
    }

    // Server slots (U1–U43)
    const server = serverByU.get(u);
    if (server) {
      const st = SERVER_TYPES.find(t => t.id === server.type);
      const size = st?.uSize ?? 1;
      const heightPx = size * 18 - 1; // -1 for border
      const color = serverSlotColor(server);
      const textCls = serverSlotText(server);
      const canRepair = onRepair && server.failureStatus === 'failed';
      const statusLabel =
        server.failureStatus === 'failed' ? 'FAIL' :
        server.failureStatus === 'repairing' ? 'RPR' :
        (server.ageStage === 'eol' ? 'EOL' : server.ageStage === 'aging' ? 'AGE' : 'OK');

      rows.push(
        <div
          key={`u${u}`}
          className={`flex items-start gap-1.5 border-b border-gray-800/50`}
          style={{ height: `${heightPx}px` }}
          onMouseEnter={() => setHoveredU(u)}
          onMouseLeave={() => setHoveredU(null)}
        >
          <span className="w-6 text-right text-[9px] text-gray-600 shrink-0 font-mono mt-[3px]">{uLabel}</span>
          <div
            className={`flex-1 h-full rounded-sm border ${color} flex items-center justify-between gap-1 pl-1.5 pr-1 transition-opacity
              ${isHovered ? 'ring-1 ring-white/20' : ''}
              ${canRepair ? 'cursor-pointer hover:ring-1 hover:ring-amber-400' : 'cursor-default'}`}
            style={{ backgroundImage: thermalTint }}
            onClick={() => canRepair && onRepair!(server.id)}
          >
            <span className={`text-[9px] font-medium leading-none truncate ${textCls}`}>
              {st?.name ?? server.type}
            </span>
            <span className="flex items-center gap-1 shrink-0">
              <ServerLeds server={server} paused={isPaused} />
              {server.failureStatus !== 'failed' && (() => {
                const util = Math.max(0, Math.min(1.15, server.utilizationRate ?? 0.75));
                const pct = Math.min(100, (util / 1.15) * 100);
                const barColor = util >= 1.0 ? '#ef4444' : util >= 0.8 ? '#f59e0b' : '#22c55e';
                return (
                  <span title={`Load ${Math.round(util * 100)}%`} className="inline-flex items-end h-[11px] w-[3px] bg-gray-700/70 rounded-sm overflow-hidden">
                    <span style={{ width: '100%', height: `${pct}%`, background: barColor }} />
                  </span>
                );
              })()}
              <span className={`text-[8px] font-bold leading-none ${textCls}`}>{statusLabel}</span>
            </span>
          </div>
        </div>
      );
    } else {
      // Empty server slot
      rows.push(
        <div
          key={`u${u}`}
          className="flex items-center gap-1.5 h-[18px] border-b border-gray-800/50"
          onMouseEnter={() => setHoveredU(u)}
          onMouseLeave={() => setHoveredU(null)}
        >
          <span className="w-6 text-right text-[9px] text-gray-600 shrink-0 font-mono">{uLabel}</span>
          <div className="flex-1 h-[14px] rounded-sm border border-dashed border-gray-700/60 bg-gray-800/20" />
        </div>
      );
    }
  }

  // Hover tooltip data
  const hoveredServer = hoveredU !== null ? Array.from(serverByU.entries()).find(([topU, s]) => {
    const st = SERVER_TYPES.find(t => t.id === s.type);
    const size = st?.uSize ?? 1;
    return hoveredU <= topU && hoveredU > topU - size;
  })?.[1] : null;
  const hoveredType = hoveredServer ? SERVER_TYPES.find(t => t.id === hoveredServer.type) : null;

  const failed = servers.filter(s => s.failureStatus === 'failed').length;
  const repairing = servers.filter(s => s.failureStatus === 'repairing').length;
  const eol = servers.filter(s => (s.ageStage ?? 'new') === 'eol').length;
  const usedU = servers.reduce((sum, s) => {
    const st = SERVER_TYPES.find(t => t.id === s.type);
    return sum + (st?.uSize ?? 1);
  }, 0);

  // PDU power utilisation (servers + switches + rack overhead) vs PDU budget
  const pduUsed = (rack.usedPower || 0) + (rack.powerDraw || 0);
  const pduBudget = rackType.power || 0;
  const pduPct = pduBudget > 0 ? Math.min(100, (pduUsed / pduBudget) * 100) : 0;
  const pduColor = pduPct >= 95 ? 'bg-red-500' : pduPct >= 80 ? 'bg-amber-500' : 'bg-emerald-500';

  // Bandwidth: live server traffic vs TOR uplink (×2 with MLAG redundant)
  const liveTraffic = servers
    .filter(s => s.failureStatus !== 'failed')
    .reduce((sum, s) => sum + (SERVER_TYPES.find(t => t.id === s.type)?.trafficGbps ?? 0), 0);
  const uplinkGbps = torSwitch ? torSwitch.uplinkBandwidthGbps * (torSwitchRedundant ? 2 : 1) : 0;
  const bwPct = uplinkGbps > 0 ? Math.min(100, (liveTraffic / uplinkGbps) * 100) : 0;
  const bwColor = bwPct >= 95 ? 'bg-red-500' : bwPct >= 80 ? 'bg-amber-500' : 'bg-emerald-500';

  // Upstream path health (rack → TOR → HDA → Core → MMR → internet)
  const upstream: { label: string; ok: boolean }[] = networkStatus ? [
    { label: 'TOR', ok: !!torSwitch && (uplinkGbps === 0 || liveTraffic <= uplinkGbps) },
    { label: 'HDA', ok: networkStatus.floorHasHda },
    { label: 'Core', ok: networkStatus.locationHasCore },
    { label: 'MMR', ok: networkStatus.mmrBuilt },
    { label: 'Net', ok: networkStatus.externalConnectivity >= 0.999 },
  ] : [];

  return (
    <div className="bg-gray-900 border border-gray-700 rounded-lg p-3 mt-3">
      <style>{`
        @keyframes riv-blink { 0%,49% { opacity: 1 } 50%,100% { opacity: 0.18 } }
        @keyframes riv-breathe { 0%,100% { opacity: 1 } 50% { opacity: 0.5 } }
      `}</style>
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-semibold text-gray-200">{rackType.name} — Rack View</span>
        <div className="flex gap-1.5 flex-wrap justify-end text-[10px]">
          <span className="px-1.5 py-0.5 rounded bg-gray-800 text-gray-400">{usedU}/{RACK_SERVER_MAX_U}U</span>
          {failed > 0 && <span className="px-1.5 py-0.5 rounded bg-red-900 text-red-300">{failed} failed</span>}
          {repairing > 0 && <span className="px-1.5 py-0.5 rounded bg-amber-900 text-amber-300">{repairing} repairing</span>}
          {eol > 0 && <span className="px-1.5 py-0.5 rounded bg-orange-900 text-orange-300">{eol} EOL</span>}
        </div>
      </div>

      {/* PDU power + bandwidth bars */}
      <div className="grid grid-cols-2 gap-3 mb-2">
        <div>
          <div className="flex justify-between text-[10px] mb-0.5">
            <span className="text-gray-400">PDU power</span>
            <span className={pduPct >= 95 ? 'text-red-400' : 'text-gray-300'}>{pduUsed.toFixed(1)}/{pduBudget} kW</span>
          </div>
          <div className="w-full bg-gray-700 rounded-full h-1.5"><div className={`${pduColor} h-1.5 rounded-full`} style={{ width: `${pduPct}%` }} /></div>
        </div>
        <div>
          <div className="flex justify-between text-[10px] mb-0.5">
            <span className="text-gray-400">Uplink{torSwitchRedundant ? ' (MLAG)' : ''}</span>
            <span className={bwPct >= 95 ? 'text-red-400' : 'text-gray-300'}>{liveTraffic.toFixed(1)}/{uplinkGbps || '—'} Gbps</span>
          </div>
          <div className="w-full bg-gray-700 rounded-full h-1.5"><div className={`${bwColor} h-1.5 rounded-full`} style={{ width: `${bwPct}%` }} /></div>
        </div>
      </div>

      {/* Upstream network path */}
      {upstream.length > 0 && (
        <div className="flex items-center gap-1 mb-2 text-[10px]">
          <span className="text-gray-500">Path:</span>
          {upstream.map((s, i) => (
            <React.Fragment key={s.label}>
              {i > 0 && <span className="text-gray-600">›</span>}
              <span className={`px-1 rounded ${s.ok ? 'bg-emerald-900/60 text-emerald-300' : 'bg-red-900/60 text-red-300'}`}>{s.label}</span>
            </React.Fragment>
          ))}
        </div>
      )}

      {/* Rack column — scrollable */}
      <div className="overflow-y-auto max-h-80 pr-1" style={{ scrollbarWidth: 'thin' }}>
        <div className="flex flex-col">
          {rows}
        </div>
      </div>

      {/* Hover detail */}
      {hoveredServer && hoveredType && (
        <div className="mt-2 p-2 bg-gray-800 border border-gray-600 rounded text-xs space-y-0.5">
          <div className="font-semibold text-white">{hoveredType.name}</div>
          <div className="text-gray-400">
            Status: <span className={serverSlotText(hoveredServer)}>{
              hoveredServer.failureStatus === 'failed' ? 'Failed' :
              hoveredServer.failureStatus === 'repairing' ? 'Repairing' :
              'Operational'
            }</span>
          </div>
          <div className="text-gray-400">
            Age: <span className="text-gray-200">{AGE_LABEL[hoveredServer.ageStage ?? 'new']}</span>
            {hoveredServer.ageEfficiencyMultiplier != null && (
              <span className="ml-1 text-gray-500">({(hoveredServer.ageEfficiencyMultiplier * 100).toFixed(0)}% eff.)</span>
            )}
          </div>
          {formatCurrency && (
            <div className="text-gray-400">Revenue: <span className="text-green-400">{formatCurrency(hoveredType.revenue)}/day</span></div>
          )}
          <div className="text-gray-400">Power: <span className="text-gray-200">{hoveredType.powerDraw} kW</span> · Traffic: <span className="text-gray-200">{hoveredType.trafficGbps}G</span> <span className="text-gray-500">({hoveredType.nicSpeedGbps}G NIC)</span></div>
          <div className="text-gray-400">Satisfaction: <span className={(hoveredServer.satisfactionScore ?? 100) >= 70 ? 'text-emerald-400' : (hoveredServer.satisfactionScore ?? 100) >= 40 ? 'text-amber-400' : 'text-red-400'}>{Math.round(hoveredServer.satisfactionScore ?? 100)}/100</span></div>
          {hoveredServer.failureStatus === 'failed' && onRepair && (
            <div className="text-amber-400 font-semibold">Click to repair</div>
          )}
          {hoveredServer.failureStatus === 'repairing' && hoveredServer.repairCompletesAt != null && (
            <div className="text-amber-300">
              Done in ~{Math.max(0, Math.ceil((hoveredServer.repairCompletesAt - gameTime) / 60))}s
            </div>
          )}
        </div>
      )}

      {/* Legend */}
      <div className="flex flex-wrap gap-2 mt-2 text-[10px] text-gray-500">
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-emerald-900 border border-emerald-700 inline-block" />OK</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-yellow-800 border border-yellow-600 inline-block" />Aging</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-orange-800 border border-orange-500 inline-block" />EOL</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-amber-800 border border-amber-500 inline-block" />Repairing</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-red-800 border border-red-500 inline-block" />Failed</span>
      </div>
    </div>
  );
};

export default RackInteriorView;
